import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  confirmPhoneLogin,
  initiatePhoneLogin,
  logoutSession,
  registerPhoneAccount,
  selectLoginVenue,
  PhoneLoginCustomer,
  PhoneLoginVenueOption,
} from '../api/auth';
import { AUTH_EXPIRED_EVENT } from '../api/client';

// Реальная авторизация PWA — вход по телефону (Plusofon flash-call). Секрет сессии живёт в httpOnly-cookie
// customerSession (JS не читает — XSS-устойчиво). В localStorage храним ТОЛЬКО профиль (id/venue/имя, не
// секрет) для персистентности между перезагрузками (whoami-эндпоинта нет — LUNAA-99). На 401 сбрасываем.
const STORAGE_KEY = 'lunit_pwa_customer';

export interface AuthUser {
  id: string;
  venueId: string;
  firstName: string;
}

type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

// Результат подтверждения кода для UI: либо вошли, либо надо выбрать заведение.
export type ConfirmOutcome =
  | { status: 'authenticated' }
  | { status: 'select_venue'; identityToken: string; venues: PhoneLoginVenueOption[] }
  | { status: 'register'; identityToken: string };

interface AuthContextType {
  user: AuthUser | null;
  status: AuthStatus;
  isAuthenticated: boolean;
  requestCode: (phone: string) => Promise<{ retryAfterSeconds: number }>;
  confirmCode: (phone: string, code: string) => Promise<ConfirmOutcome>;
  selectVenue: (identityToken: string, venueId: string) => Promise<void>;
  register: (identityToken: string, firstName: string, referralCode?: string) => Promise<PhoneLoginCustomer>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

function loadStored(): AuthUser | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<AuthUser | null>(() => loadStored());
  const [status, setStatus] = useState<AuthStatus>(() => (loadStored() ? 'authenticated' : 'anonymous'));

  const applyUser = useCallback((c: PhoneLoginCustomer) => {
    const u: AuthUser = { id: c.id, venueId: c.venueId, firstName: c.firstName };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(u));
    setUser(u);
    setStatus('authenticated');
  }, []);

  const clear = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    // react-query держит личные данные (профиль/история/уровни) — чистим, иначе следующий юзер
    // на общем устройстве увидит чужой кэш.
    queryClient.clear();
    setUser(null);
    setStatus('anonymous');
  }, [queryClient]);

  // 401 из любого запроса (протухшая cookie) → сессия недействительна → anonymous, guard уводит на login.
  useEffect(() => {
    const onExpired = () => clear();
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
  }, [clear]);

  const requestCode = useCallback((phone: string) => initiatePhoneLogin(phone), []);

  const confirmCode = useCallback(
    async (phone: string, code: string): Promise<ConfirmOutcome> => {
      const res = await confirmPhoneLogin(phone, code);
      if (res.status === 'authenticated') {
        applyUser(res.customer);
        return { status: 'authenticated' };
      }
      if (res.status === 'register') {
        return { status: 'register', identityToken: res.identityToken };
      }
      return { status: 'select_venue', identityToken: res.identityToken, venues: res.venues };
    },
    [applyUser]
  );

  const selectVenue = useCallback(
    async (identityToken: string, venueId: string) => {
      const res = await selectLoginVenue(identityToken, venueId);
      applyUser(res.customer);
    },
    [applyUser]
  );

  const register = useCallback(
    async (identityToken: string, firstName: string, referralCode?: string) => {
      const res = await registerPhoneAccount(identityToken, firstName, referralCode);
      applyUser(res.customer);
      return res.customer;
    },
    [applyUser]
  );

  const logout = useCallback(async () => {
    try {
      await logoutSession();
    } catch {
      // выход не должен падать из-за сети — cookie всё равно чистим локально ниже
    }
    clear();
  }, [clear]);

  return (
    <AuthContext.Provider
      value={{
        user,
        status,
        isAuthenticated: status === 'authenticated',
        requestCode,
        confirmCode,
        selectVenue,
        register,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
